import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";

interface ScrollableTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  error?: boolean;
}

export const ScrollableTextarea: React.FC<ScrollableTextareaProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  maxLength = 500,
  error,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Auto-resize textarea to fit all content
  useEffect(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    // Reset height to get accurate scrollHeight
    textarea.style.height = "auto";
    // Set height to full content height (no max limit on textarea itself)
    textarea.style.height = Math.max(textarea.scrollHeight, 80) + "px";
  }, [value]);

  // Auto-focus on mount
  useEffect(() => {
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        border: error ? "1px solid #d32f2f" : "1px solid #e0e0e0",
        borderRadius: "12px",
        backgroundColor: disabled ? "#f5f5f5" : "#fff",
        overflow: "hidden",
        width: "100%",
        padding: "2%",
        "&:focus-within": {
          borderColor: error ? "#d32f2f" : "#8b5cf6",
        },
      }}
    >
      <Box
        ref={wrapperRef}
        sx={{
          maxHeight: "150px",
          minHeight: "100px",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          // CRITICAL for iOS scrolling
          touchAction: "pan-y",
          position: "relative",
          // Ensure scrollbar is visible
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "3px",
          },
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          style={{
            width: "100%",
            minHeight: "80px",
            border: "none",
            outline: "none",
            padding: "12px",
            fontSize: "16px",
            fontFamily: "inherit",
            resize: "none",
            backgroundColor: "transparent",
            color: disabled ? "#999" : "#000",
            // CRITICAL: Remove all scrolling from textarea
            overflow: "hidden",
            lineHeight: "1.5",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
};

export default ScrollableTextarea;
